import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Moon, Sun } from "lucide-react";
import { ip_back } from "../../config";
import { LanguageShortcutFab } from "../../components/LanguageShortcutFab";
import AgentMark from "../../digiping/AgentMark";
import smartttWordmark from "../../assets/smarttt-wordmark.png";
import { resolveClubLogoUrl } from "../../utils/resolveClubImageUrl";
import {
  applyInvitePageLanguage,
  markInviteUiLangOverride,
} from "../../utils/clubCountryLanguage";
import { useBrand } from "../../context/BrandContext";
import "./ClubJoinLanding.css";

/**
 * Public QR / link landing: choose Player or Parent, then open the matching
 * Pingflo profiling path. Email invites with tokens skip this and deep-link.
 */
export default function ClubJoinLanding() {
  const { t, i18n } = useTranslation();
  const { themeMode, setThemeMode } = useBrand();
  const navigate = useNavigate();
  const { clubCode: paramClubCode } = useParams();
  const [searchParams] = useSearchParams();
  const clubCode = (paramClubCode || searchParams.get("club_code") || "").trim();

  const [club, setClub] = useState(null);
  const [loading, setLoading] = useState(Boolean(clubCode));
  const [error, setError] = useState("");
  const [logoBroken, setLogoBroken] = useState(false);

  const querySuffix = useMemo(() => {
    const next = new URLSearchParams(searchParams);
    if (clubCode && !next.get("club_code")) next.set("club_code", clubCode);
    const s = next.toString();
    return s ? `?${s}` : "";
  }, [searchParams, clubCode]);

  useEffect(() => {
    const token = searchParams.get("invitation_token") || searchParams.get("token");
    if (!token || !clubCode) return;
    navigate(`/join/${encodeURIComponent(clubCode)}/player${querySuffix}`, { replace: true });
  }, [clubCode, navigate, querySuffix, searchParams]);

  useEffect(() => {
    if (!clubCode) {
      setLoading(false);
      setError(
        t(
          "clubJoinMissingCode",
          "This invite link is missing the club. Ask your club for a fresh QR or link."
        )
      );
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    setLogoBroken(false);
    const url = `${ip_back}api/club/join/${encodeURIComponent(clubCode)}`;
    fetch(url)
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error("not found"))))
      .then((body) => {
        if (cancelled) return;
        const data = body?.data || body?.club || body;
        if (!data?.club_id && !data?.club_name) {
          setError(t("clubJoinNotFound", "We could not find this club."));
          setClub(null);
          return;
        }
        setClub(data);
        applyInvitePageLanguage(i18n, data);
      })
      .catch(() => {
        if (cancelled) return;
        setError(t("clubJoinNotFound", "We could not find this club."));
        setClub(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Only re-fetch when the club code changes — language picks must not reset the club default.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n/t intentionally omitted
  }, [clubCode]);

  const clubName = club?.club_name || club?.brand_name || t("club", "Club");
  const clubLogoSrc = resolveClubLogoUrl(club?.club_image, {
    logo_url: club?.logo_url,
  });
  const clubInitials = useMemo(() => {
    const parts = String(clubName || "")
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (!parts.length) return "TT";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
  }, [clubName]);

  const goPlayer = () => {
    if (!clubCode) return;
    navigate(`/join/${encodeURIComponent(clubCode)}/player${querySuffix}`);
  };

  const goParent = () => {
    const qs = new URLSearchParams();
    if (club?.club_id) qs.set("club", String(club.club_id));
    if (clubCode) qs.set("club_code", clubCode);
    const lng = (i18n.language || "en").split("-")[0];
    if (lng) qs.set("lng", lng);
    navigate(`/parent-profiling?${qs.toString()}`);
  };

  return (
    <div className={`cjl cjl--${themeMode === "dark" ? "dark" : "light"}`}>
      <div className="cjl__glow cjl__glow--a" aria-hidden />
      <div className="cjl__glow cjl__glow--b" aria-hidden />
      <div className="cjl__chrome">
        <div
          className="cjl__theme"
          role="group"
          aria-label={t("themeMode", "Theme Mode")}
        >
          <button
            type="button"
            className={`cjl__theme-btn${themeMode === "light" ? " is-active" : ""}`}
            aria-pressed={themeMode === "light"}
            title={t("lightMode", "Light")}
            onClick={() => setThemeMode("light")}
          >
            <Sun size={15} strokeWidth={2.2} aria-hidden />
          </button>
          <button
            type="button"
            className={`cjl__theme-btn${themeMode === "dark" ? " is-active" : ""}`}
            aria-pressed={themeMode === "dark"}
            title={t("darkMode", "Dark")}
            onClick={() => setThemeMode("dark")}
          >
            <Moon size={15} strokeWidth={2.2} aria-hidden />
          </button>
        </div>
        <div className="cjl__lang">
          <LanguageShortcutFab
            variant="invite"
            onPicked={markInviteUiLangOverride}
          />
        </div>
      </div>

      <main className="cjl__stage">
        <header className="cjl__brand">
          {clubLogoSrc && !logoBroken ? (
            <img
              className="cjl__logo"
              src={clubLogoSrc}
              alt={clubName}
              onError={() => setLogoBroken(true)}
            />
          ) : (
            <div className="cjl__logo cjl__logo--initials" aria-hidden={!clubName}>
              {loading ? "…" : clubInitials}
            </div>
          )}
          <h1 className="cjl__title">
            {loading
              ? t("clubJoinLoading", "Opening your club…")
              : t("clubJoinTitle", { club: clubName, defaultValue: `Join ${clubName}` })}
          </h1>
          <p className="cjl__lead">
            {t(
              "clubJoinLeadShort",
              "Choose how you join — Pingflo guides the rest."
            )}
          </p>
        </header>

        {error ? (
          <p className="cjl__error" role="alert">
            {error}
          </p>
        ) : null}

        {!error ? (
          <div className="cjl__paths" role="list">
            <button
              type="button"
              className="cjl__path cjl__path--player"
              role="listitem"
              onClick={goPlayer}
              disabled={loading || !clubCode}
            >
              <span className="cjl__path-agents" aria-hidden>
                <AgentMark agentId="pingflo" size={40} showGlow title="Pingflo" />
              </span>
              <span className="cjl__path-copy">
                <span className="cjl__path-kicker">{t("clubJoinPlayerKicker", "I play")}</span>
                <span className="cjl__path-name">{t("clubJoinPlayerTitle", "Player account")}</span>
                <span className="cjl__path-desc">
                  {t(
                    "clubJoinPlayerDescShort",
                    "SMART profile, training, and your path at the table."
                  )}
                </span>
              </span>
              <span className="cjl__path-cta">{t("clubJoinPlayerCta", "Continue as player")}</span>
            </button>

            <button
              type="button"
              className="cjl__path cjl__path--parent"
              role="listitem"
              onClick={goParent}
              disabled={loading || !clubCode}
            >
              <span className="cjl__path-agents" aria-hidden>
                <AgentMark agentId="pingwi" size={36} showGlow title="Pingwi" />
              </span>
              <span className="cjl__path-copy">
                <span className="cjl__path-kicker">
                  {t("clubJoinParentKicker", "I accompany a child")}
                </span>
                <span className="cjl__path-name">
                  {t("clubJoinParentTitle", "Parent / tutor account")}
                </span>
                <span className="cjl__path-desc">
                  {t(
                    "clubJoinParentDescShort",
                    "Register a child, follow progress, and RSVP."
                  )}
                </span>
              </span>
              <span className="cjl__path-cta">{t("clubJoinParentCta", "Continue as parent")}</span>
            </button>
          </div>
        ) : null}

        <p className="cjl__foot">
          {t("alreadyHaveAccount", "Already have an account?")}{" "}
          <Link to="/login" className="cjl__foot-link">
            {t("clubJoinFootLogin", "Log in")}
          </Link>
        </p>
      </main>

      <footer className="cjl__powered">
        <span className="cjl__powered-label">
          {t("clubJoinPoweredBy", "Powered by")}
        </span>
        <img
          className="cjl__powered-logo"
          src={smartttWordmark}
          alt="SmartTT Club"
        />
      </footer>
    </div>
  );
}
